import {
  AGREEMENT_TYPES,
  APP_MINIMUM_BALANCE,
  SystemCode,
  DELIVERY_MAIL_CHECK,
  FundNoteSell,
  MONEY_MARKET_EVENT_FEE,
  MONEY_MARKET_EVENT_GATE,
  MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
  NON_FIDUCIARY_PLAN,
  DELIVERY_IN_KIND,
  DELIVERY_MAIL_CHECK_TO_SCHOOL,
} from 'src/settings/settings';
import { isAPP } from '../../advice/advice-utility';
import {
  checkACHBankServiceFeature,
  checkBankServiceFeature,
  checkFoundFeaturesByCode,
  checkWireBankServiceFeature,
  DISABLE_ON_MATCH,
  isTransactionAssociatedForFundingBank,
  isTransactionRestrictedForFundingBank,
  isTransactionPendingStatusAgreements,
  missingRedemptionFeature,
  updateFundNote,
} from '../shared/shared-fund-notes-utility';
import { isRAS } from 'src/utils/advice/advice-utility';

export function updateSellFundNotesForFund(
  deliveryMethod,
  fund,
  isCheckWithMultipleAddress,
  selectedBankAgreementMap,
  selectedBank
) {
  // If client has a fund under a registration that the client cannot associate to the chosen bank for that fund,
  // "Cannot Link Bank Signature Required" will be added to the notes section. The popover message will have a
  // link that directs client to a form that opens in a new tab
  updateFundNote(
    fund,
    isTransactionRestrictedForFundingBank(fund, selectedBankAgreementMap) &&
      deliveryMethod !== DELIVERY_MAIL_CHECK &&
      deliveryMethod !== DELIVERY_MAIL_CHECK_TO_SCHOOL &&
      deliveryMethod !== DELIVERY_IN_KIND,
    FundNoteSell.CANNOT_LINK_BANK,
    DISABLE_ON_MATCH
  );
  // If RAS+APP client selects In-Kind Transfer as their delivery option,
  // RAS accounts will be disabled and an "In-Kind Transfer Unavailable" note
  // will be added to the notes section.
  updateFundNote(
    fund,
    deliveryMethod === DELIVERY_IN_KIND,
    FundNoteSell.NO_RAS_IN_KIND_TRANSFER,
    DISABLE_ON_MATCH
  );
  //is APP fund with less than 10K balance
  updateFundNote(
    fund,
    isAPP(fund.productType) &&
      fund.balanceAsOf > 0 &&
      fund.balanceAsOf <= APP_MINIMUM_BALANCE,
    FundNoteSell.INELIGIBLE_BALANCE
  );
  updateFundNote(
    fund,
    isTransactionPendingStatusAgreements(fund, selectedBankAgreementMap),
    FundNoteSell.PENDING_STATUS,
    DISABLE_ON_MATCH
  );
  // If client has a fund that has a "Stop Transfer" service in effect (ServiceCode: 31)
  // "Stop Transfer" will be added to the notes section.
  updateFundNote(
    fund,
    checkFoundFeaturesByCode(fund, SystemCode.STOP_TRANSFER_ID),
    FundNoteSell.STOP_TRANSFER,
    DISABLE_ON_MATCH
  );

  // If client has a fund that has a "Penalty Withholding Flag" service in effect (ServiceCode: 32)
  // "IRS Tax Withholding" will be added to the notes section.
  updateFundNote(
    fund,
    checkFoundFeaturesByCode(fund, SystemCode.PENALTY_WITHOLDING),
    FundNoteSell.PENALTY_WITHHOLDING,
    DISABLE_ON_MATCH
  );

  // If client has a fund that has a "Automated Systems Stop Redemption" service in effect (ServiceCode: 46)
  // "Unavailable Online" will be added to the notes section.
  updateFundNote(
    fund,
    checkFoundFeaturesByCode(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_REDEMPTION_ID
    ),
    FundNoteSell.AUTOMATED_SYSTEMS_STOP_REDEMPTION,
    DISABLE_ON_MATCH
  );

  // If client has selected a bank that is missing the selected service feature for this agreement than block the fund
  updateFundNote(
    fund,
    checkBankServiceFeature(fund, deliveryMethod, selectedBank) &&
      fund.restrictedByAddressChange,
    FundNoteSell.BANK_MISSING_ALL_SERVICE_FEATURES,
    DISABLE_ON_MATCH
  );

  // If client has selected a bank that is missing the selected service feature for this agreement than block the fund
  updateFundNote(
    fund,
    checkBankServiceFeature(fund, deliveryMethod, selectedBank) &&
      !fund.restrictedByAddressChange &&
      deliveryMethod !== DELIVERY_MAIL_CHECK,
    FundNoteSell.BANK_MISSING_SERVICE_FEATURES,
    DISABLE_ON_MATCH
  );

  // If client has selected a bank that is missing the selected service feature for this agreement than block the fund
  updateFundNote(
    fund,
    checkACHBankServiceFeature(fund, deliveryMethod, selectedBank) &&
      !fund.restrictedByAddressChange,
    FundNoteSell.BANK_MISSING_ACH_SERVICE_FEATURES,
    DISABLE_ON_MATCH
  );

  // If client has selected a bank that is missing the selected service feature for this agreement than block the fund
  updateFundNote(
    fund,
    checkACHBankServiceFeature(fund, deliveryMethod, selectedBank) &&
      fund.restrictedByAddressChange,
    FundNoteSell.BANK_MISSING_ACH_MAIL_SERVICE_FEATURES,
    DISABLE_ON_MATCH
  );

  // If client has selected a bank that is missing the selected service feature for this agreement than block the fund
  updateFundNote(
    fund,
    checkWireBankServiceFeature(fund, deliveryMethod, selectedBank) &&
      !fund.restrictedByAddressChange,
    FundNoteSell.BANK_MISSING_WIRE_SERVICE_FEATURES,
    DISABLE_ON_MATCH
  );

  // If client has selected a bank that is missing the selected service feature for this agreement than block the fund
  updateFundNote(
    fund,
    checkWireBankServiceFeature(fund, deliveryMethod, selectedBank) &&
      fund.restrictedByAddressChange,
    FundNoteSell.BANK_MISSING_WIRE_MAIL_SERVICE_FEATURES,
    DISABLE_ON_MATCH
  );

  // If client has a fund that has a "Automated Systems Stop Redemption" service in effect (ServiceCode: 46)
  // "Unavailable Online" will be added to the notes section.
  updateFundNote(
    fund,
    checkFoundFeaturesByCode(
      fund,
      SystemCode.AUTOMATED_SYSTEMS_STOP_REDEMPTION_ID
    ),
    FundNoteSell.AUTOMATED_SYSTEMS_STOP_REDEMPTION,
    DISABLE_ON_MATCH
  );

  // If client has a fund in which an effectiveDate that is <15 days from the date accessing the flow
  // "Check Redemption Unavailable" will be added to the notes section
  updateFundNote(
    fund,
    fund.hasUnavailableDelivery,
    FundNoteSell.UNAVAILABLE_DELIVERY,
    deliveryMethod === DELIVERY_MAIL_CHECK && DISABLE_ON_MATCH
  );

  // If client has a fund in which balanceAsOf and equityAsOf are not equal
  // "Uncollected shares" will be added to the notes section.
  updateFundNote(
    fund,
    fund.hasUncollectedShares,
    FundNoteSell.UNCOLLECTED_SHARES
  );

  // If client has a fund in which balanceAsOf and equityAsOf are not equal and the balance is zero
  // "Uncollected shares" will be added to the notes section and the we will not allow them to trade
  updateFundNote(
    fund,
    fund.hasUncollectedShares && fund.equityAsOf === 0 && fund.balanceAsOf > 0,
    FundNoteSell.ZERO_BALANCE_WITH_UNCOLLECTED_SHARES,
    DISABLE_ON_MATCH
  );

  // If client has a fund in which restrictedAddressChanges is true and have selected MailACheck delivery method
  // "Address change pending" will be added to the notes section and that particular fund is locked.
  updateFundNote(
    fund,
    isCheckWithMultipleAddress && fund.restrictedByAddressChange,
    FundNoteSell.ADDRESS_CHANGE_PENDING,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    deliveryMethod === DELIVERY_MAIL_CHECK && fund.restrictedByAddressChange,
    FundNoteSell.ADDRESS_CHANGE_PENDING,
    DISABLE_ON_MATCH
  );

  // If client has selected any Delivery Method in step1
  // If the fund has a missing Required feature with service code 20 or has all three missing Required features then that fund is blocked
  // "Unavailable Online" will be added to the notes section.
  updateFundNote(
    fund,
    missingRedemptionFeature(fund, deliveryMethod) === 'all',
    FundNoteSell.UNAVAILABLE_FOR_REDEMPTION_ONLINE,
    DISABLE_ON_MATCH
  );

  // If client has selected Ach in step1
  // If the fund has a missing Required feature with service code 49 and the fund is associated then that fund is blocked
  // "Direct Deposit Unavailable" will be added to the notes section.
  updateFundNote(
    fund,
    missingRedemptionFeature(fund, deliveryMethod) === 'ACH' &&
      isTransactionAssociatedForFundingBank(fund, selectedBankAgreementMap),
    FundNoteSell.UNAVAILABLE_FOR_ACH_REDEMPTION_ONLINE,
    DISABLE_ON_MATCH
  );

  // If client has selected Wire in step1
  // If the fund has a missing Required feature with service code 55 and the fund is associated then that fund is blocked
  // "Wire Redemption Unavailable" will be added to the notes section.
  updateFundNote(
    fund,
    missingRedemptionFeature(fund, deliveryMethod) === 'Wire' &&
      isTransactionAssociatedForFundingBank(fund, selectedBankAgreementMap),
    FundNoteSell.UNAVAILABLE_FOR_WIRE_REDEMPTION_ONLINE,
    DISABLE_ON_MATCH
  );

  // If client has a fund that has a "Stop Mail" service in agreement level
  // checking addressChangeOnSameDay for edge case scenario (seen in prod)
  // "Address Undeliverable" will be added to the notes section.
  updateFundNote(
    fund,
    checkFoundFeaturesByCode(fund, SystemCode.STOP_MAIL_ID) &&
      !fund.addressChangedSameDay &&
      deliveryMethod !== DELIVERY_MAIL_CHECK_TO_SCHOOL,
    FundNoteSell.ADDRESS_UNDELIVERABLE,
    DISABLE_ON_MATCH
  );

  // If client has a fund with agreementType 'EDUCATION_SAVINGS_ACCOUNT'
  // "ESA-PLAN" will be added to the notes section.
  updateFundNote(
    fund,
    fund.agreementType === 'EDUCATION_SAVINGS_ACCOUNT',
    FundNoteSell.ESA_PLAN,
    DISABLE_ON_MATCH
  );

  // If client has a fund with PendingTransactions that does not exceed redeemable funds
  // "transaction pending" will be added to the notes section.
  updateFundNote(
    fund,
    fund.hasPendingTransaction && fund.redeemableFundDollars !== 0,
    FundNoteSell.PENDING
  );

  // If client has a fund with PendingTransactions and the amount exceeds redeemable funds
  // "transaction pending" will be added to the notes section.
  updateFundNote(
    fund,
    fund.hasPendingTransaction && fund.redeemableFundDollars === 0,
    FundNoteSell.PENDING_TRANSACTION,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    fund.agreementType === NON_FIDUCIARY_PLAN,
    FundNoteSell.NON_FIDUCIARY_FUND,
    DISABLE_ON_MATCH
  );

  // no more inherited IRA block in sell
  updateFundNote(
    fund,
    fund.agreementType === AGREEMENT_TYPES.TRADITIONAL_IRA_DECEASED_BENE ||
      fund.agreementType === AGREEMENT_TYPES.ROTH_IRA_DECEASED_BENE,
    FundNoteSell.INHERITED_FUND,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    fund.agreementType === AGREEMENT_TYPES.TRADITIONAL_IRA_DECEASED ||
      fund.agreementType === AGREEMENT_TYPES.ROTH_IRA_DECEASED,
    FundNoteSell.IRA_DECEASED,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    fund.agreementType === AGREEMENT_TYPES.SEP_IRA,
    FundNoteSell.SEP_IRA,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    fund.isFundAssociable &&
      deliveryMethod !== DELIVERY_MAIL_CHECK &&
      deliveryMethod !== DELIVERY_MAIL_CHECK_TO_SCHOOL &&
      deliveryMethod !== DELIVERY_IN_KIND,
    FundNoteSell.BANK_NOT_LINKED,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_GATE &&
      fund.moneyMarketReformMessagePeriod ===
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteSell.MONEY_MARKET_EVENT_GATE_ACTIVE,
    DISABLE_ON_MATCH
  );

  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_FEE &&
      fund.moneyMarketReformMessagePeriod ===
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteSell.MONEY_MARKET_EVENT_FEE_ACTIVE
  );

  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_GATE &&
      fund.moneyMarketReformMessagePeriod !==
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteSell.MONEY_MARKET_EVENT_GATE_REMOVED
  );

  updateFundNote(
    fund,
    fund.moneyMarketReformMessage &&
      fund.moneyMarketReformMessageType === MONEY_MARKET_EVENT_FEE &&
      fund.moneyMarketReformMessagePeriod !==
        MONEY_MARKET_EVENT_PERIOD_IN_EVENT,
    FundNoteSell.MONEY_MARKET_EVENT_FEE_REMOVED
  );

  updateFundNote(
    fund,
    isRAS(fund.productType) && fund.hasEarlyWithdrawalPenalty,
    FundNoteSell.RAS_EARLY_IRA_WITHDRAWAL,
    DISABLE_ON_MATCH
  );
}
